import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const KEY = 'christovibes:cookie_consent:v1';

function readCurrent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.choice || null;
  } catch { return null; }
}

export default function CookieBanner() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (pathname.startsWith('/admin')) return;
    try {
      const stored = localStorage.getItem(KEY);
      if (!stored) {
        const id = setTimeout(() => setVisible(true), 700);
        return () => clearTimeout(id);
      }
    } catch {}
  }, [pathname]);

  useEffect(() => {
    const handler = () => {
      try { localStorage.removeItem(KEY); } catch {}
      setVisible(true);
    };
    window.addEventListener('christovibes:reopen-cookie-banner', handler);
    return () => window.removeEventListener('christovibes:reopen-cookie-banner', handler);
  }, []);

  const persist = (choice) => {
    const previous = readCurrent();
    try {
      localStorage.setItem(KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    } catch {}
    try {
      window.dispatchEvent(new CustomEvent('christovibes:consent-changed', { detail: { choice } }));
    } catch {}
    setVisible(false);
    // Downgrading from personalised ("all") to essentials must reset
    // any already-served personalised ads. Full reload is the safest reset.
    if (previous === 'all' && choice !== 'all') {
      setTimeout(() => { try { window.location.reload(); } catch {} }, 120);
    }
  };

  if (!visible || pathname.startsWith('/admin')) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-50 w-[min(720px,calc(100%-2rem))] animate-in fade-in slide-in-from-bottom-4"
      style={{ animationDuration: '400ms' }}
    >
      <div className="relative rounded-2xl border border-white/10 bg-[#0F1115]/95 backdrop-blur-xl shadow-2xl shadow-black/60 p-5 sm:p-6">
        <button
          data-testid="cookie-dismiss"
          onClick={() => persist('essential')}
          className="absolute top-3 right-3 p-1.5 rounded-full text-[#64748B] hover:text-white hover:bg-white/5"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
            <Cookie size={20} className="text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-base font-semibold text-white mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              A gentle heads-up about cookies
            </p>
            <p className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
              Christovibes uses only the cookies needed to run the stream and remember your preferences.
              No advertising, no cross-site tracking.{' '}
              <Link to="/cookies" className="text-[#D4AF37] hover:underline">Read our Cookie Policy</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                data-testid="cookie-accept"
                onClick={() => persist('all')}
                className="bg-[#D4AF37] hover:bg-[#E8C24D] text-[#0F1115] font-semibold text-sm rounded-full px-5 py-2.5 transition-colors active:scale-95"
              >
                Accept all
              </button>
              <button
                data-testid="cookie-essential"
                onClick={() => persist('essential')}
                className="text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 rounded-full px-5 py-2.5 text-sm font-medium transition-colors active:scale-95"
              >
                Only essential
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
